import React, { useState, useEffect, useRef, lazy } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Truck, HeartHandshake, Award, Target, Heart, Gift } from 'lucide-react';
import Footer from '@/components/Footer';
import ShopifyBuyButton from '@/components/ShopifyBuyButton';
import LaunchCountdown from '@/components/LaunchCountdown';
import { ScrollHero } from '@/components/ScrollHero';
import { Model3DUploader } from '@/components/Model3DUploader';
import workoutBg from '@/assets/workout-bg.jpg';

import { Toaster } from '@/components/ui/toaster';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { LazySection } from '@/components/LazySection';

const PatentShowcase = lazy(() => import('@/components/PatentShowcase').then(m => ({ default: m.PatentShowcase })));

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  console.log('[Home] Component rendering...');
  const [currentModelUrl, setCurrentModelUrl] = useState<string>('https://aogzcyknlimbgcwjukah.supabase.co/storage/v1/object/public/3d-models/protien_jar.glb');
  const counterValueEl = useRef<HTMLSpanElement>(null);
  const counterRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  const animateCounter = () => {
    if (hasAnimated.current) return;
    hasAnimated.current = true;
    const duration = 2500;
    const startTime = performance.now();
    const endValue = 240;
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.round(endValue * easeOut);
      const el = counterValueEl.current;
      if (el) el.textContent = String(currentValue);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.5) animateCounter();
      });
    }, { threshold: 0.5 });
    if (counterRef.current) observer.observe(counterRef.current);
    return () => { if (counterRef.current) observer.unobserve(counterRef.current); };
  }, []);

  const handleModelUploaded = (url: string) => setCurrentModelUrl(url);
  const scrollToProducts = () => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  const formatPrice = (price: number) => new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK' }).format(price / 100);

  return (
    <div className="min-h-screen bg-background">
      <ScrollHero modelUrl={currentModelUrl} />

      <div className="hidden">
        <Model3DUploader onModelUploaded={handleModelUploaded} />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center py-12 md:py-0">
        <div className="absolute inset-0 overflow-hidden">
          <img src="/lovable-uploads/91d52591-8b86-4b1a-833d-fd54fd455527.png" alt="PEVEO - Sport og ytelse" className="w-full h-[110%] object-cover parallax-bg" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40"></div>
        </div>

        <div className="container mx-auto px-4 relative z-0">
          <div className="max-w-3xl">
            <div className="bg-white/10 backdrop-blur-md p-4 md:p-6 lg:p-8 mb-6 md:mb-8 lg:mb-10 rounded-xl border border-white/20">
              <div className="flex items-center justify-center gap-2 mb-2 md:mb-3">
                <Heart className="h-5 w-5 md:h-6 md:w-6 text-red-500 fill-red-500" style={{ animation: 'heartbeat 0.8s infinite' }} />
                <span className="text-white font-semibold text-base md:text-lg">For en god sak</span>
              </div>
              <p className="text-white/90 text-sm md:text-base lg:text-lg leading-relaxed">
                Vi skal alltid være billigst på sammenlignbare produkter - sjekk gjerne andre nettsider!
                Det overskuddet vi får investeres tilbake i startup-selskapet Stoad, for å lage verdens beste treningsmaskiner for muskelvekst.
              </p>
            </div>

            <div className="mb-6 md:mb-8 lg:mb-10">
              <LaunchCountdown />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-start">
              <Button size="lg" className="px-6 md:px-8 lg:px-10 py-3 md:py-4 text-base md:text-lg font-semibold" onClick={scrollToProducts}>
                Se våre produkter
                <ArrowRight className="ml-2 md:ml-3 h-5 w-5 md:h-6 md:w-6" />
              </Button>
              <Button size="lg" variant="outline" className="px-6 md:px-8 lg:px-10 py-3 md:py-4 text-base md:text-lg font-semibold border-white/30 text-white hover:bg-white/10" onClick={() => window.location.href = "mailto:kundeservice@peveo.no"}>
                Kontakt oss
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Research Evidence Banner */}
      <section className="py-6 md:py-8 bg-muted relative z-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 max-w-5xl mx-auto">
            <div className="text-center flex-1">
              <p className="text-base sm:text-lg md:text-xl text-foreground leading-relaxed">
                Alle produktene våre har en fagfellevurdert forskningsartikkel – les bevisene og sammenlign oss gjerne, for vi er best på både innhold og pris.
              </p>
            </div>
            <div className="bg-primary/10 px-4 py-3 rounded-md border border-primary/20">
              <p className="text-primary font-semibold text-sm md:text-base whitespace-nowrap">
                🚚 Gratis frakt over 800 kr
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section id="products" className="py-12 md:py-16 relative z-10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-orbit font-bold text-foreground mb-3 md:mb-4">
              Våre <span className="gradient-text">Produkter</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base md:text-lg px-4">
              Hvert produkt er nøye utviklet med optimal dosering av vitenskapelig dokumenterte ingredienser
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* PWO Stim-Free */}
            <Card className="group bg-card border border-border hover:border-primary/40 rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="aspect-square mb-4 overflow-hidden rounded-lg bg-muted">
                  <img src="/lovable-uploads/f334e74f-31c0-4a8f-9e2d-1377d9cfa607.png" alt="Pre-Workout" className="w-full max-w-full h-full object-cover md:group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2 font-orbit">Peveo Competition PWO-Stim-Free</h3>
                <p className="text-muted-foreground text-xs mb-3 leading-relaxed min-h-[2.5rem] flex items-center">15g citrulline malate for optimal ytelse</p>
                <p className="text-primary text-xl font-bold mb-4">299 kr</p>
                <div className="space-y-2 mt-auto">
                  <ShopifyBuyButton productId="9358247428327" componentId="product-component-home-preworkout" domain="6pjebd-jw.myshopify.com" storefrontAccessToken="24c563b5bb4fa1b2d3b84fcb8e32090c" className="w-full" iframeTitle="Kjøp PEVEO Pre-Workout - Legg til i handlekurv" />
                  <Button asChild variant="outline" className="w-full text-xs py-1">
                    <Link to="/products/pre-workout">Les mer</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Maxed */}
            <Card className="group bg-card border-2 border-primary/30 hover:border-primary rounded-lg shadow-sm hover:shadow-lg hover:shadow-primary/10 transition-all duration-300 overflow-hidden">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="aspect-square mb-4 overflow-hidden rounded-lg bg-muted">
                  <img src="/lovable-uploads/64b730a99bba1.png" alt="Pre-Workout Maxed" className="w-full max-w-full h-full object-cover scale-110 md:group-hover:scale-[1.2] transition-transform duration-500" loading="lazy" />
                </div>
                <h3 className="text-lg font-semibold text-primary mb-2 font-orbit">Peveo Maxed</h3>
                <p className="text-muted-foreground text-xs mb-3 leading-relaxed min-h-[2.5rem] flex items-center">Med 350mg koffein og beta-alanine</p>
                <p className="text-primary text-xl font-bold mb-4">499 kr</p>
                <div className="space-y-2 mt-auto">
                  <ShopifyBuyButton productId="9440709116135" componentId="product-component-home-preworkout-maxed" domain="6pjebd-jw.myshopify.com" storefrontAccessToken="24c563b5bb4fa1b2d3b84fcb8e32090c" className="w-full" iframeTitle="Kjøp PEVEO Pre-Workout Maxed - Legg til i handlekurv" />
                  <Button asChild variant="outline" className="w-full text-xs py-1">
                    <Link to="/products/pre-workout-maxed">Les mer</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Shaker */}
            <Card className="group bg-card border border-border hover:border-primary/40 rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="aspect-square mb-4 overflow-hidden rounded-lg bg-muted">
                  <img src="/lovable-uploads/7df6c9d3-dae1-4c10-b933-4f370133ca21.png" alt="Shaker" className="w-full max-w-full h-full object-cover md:group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2 font-orbit">Shaker - 600ml</h3>
                <p className="text-muted-foreground text-xs mb-3 leading-relaxed min-h-[2.5rem] flex items-center">100% lekkasjesikker 600ml BPA-fri</p>
                <p className="text-primary text-xl font-bold mb-4">{formatPrice(3900)}</p>
                <div className="space-y-2 mt-auto">
                  <ShopifyBuyButton productId="9358248444135" componentId="product-component-home-shaker" domain="6pjebd-jw.myshopify.com" storefrontAccessToken="24c563b5bb4fa1b2d3b84fcb8e32090c" className="w-full" iframeTitle="Kjøp PEVEO Shaker - Legg til i handlekurv" />
                  <Button asChild variant="outline" className="w-full text-xs py-1">
                    <Link to="/products/shaker">Les mer</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Chalk */}
            <Card className="group bg-card border border-border hover:border-primary/40 rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="aspect-square mb-4 overflow-hidden rounded-lg bg-muted">
                  <img src="/lovable-uploads/bca1d59d-3f09-4e77-b206-2dc5ddd54fae.png" alt="Liquid Chalk" className="w-full max-w-full h-full object-cover md:group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2 font-orbit">Liquid Chalk - 200ml</h3>
                <p className="text-muted-foreground text-xs mb-3 leading-relaxed min-h-[2.5rem] flex items-center">200ml med høyt kalkinnhold</p>
                <p className="text-primary text-xl font-bold mb-4">{formatPrice(4800)}</p>
                <div className="space-y-2 mt-auto">
                  <ShopifyBuyButton productId="9358249001191" componentId="product-component-home-chalk" domain="6pjebd-jw.myshopify.com" storefrontAccessToken="24c563b5bb4fa1b2d3b84fcb8e32090c" className="w-full" iframeTitle="Kjøp PEVEO Chalk - Legg til i handlekurv" />
                  <Button asChild variant="outline" className="w-full text-xs py-1">
                    <Link to="/products/chalk">Les mer</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Tennis socks */}
            <Card className="group bg-card border border-border hover:border-primary/40 rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="aspect-square mb-4 overflow-hidden rounded-lg bg-muted">
                  <img src="/lovable-uploads/987cc678-2409-4e5c-a53f-2e3178f82fe5.png" alt="Tennissokker (10-pakning)" className="w-full max-w-full h-full object-cover md:group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2 font-orbit">Tennissokker<br />(10-pakning)</h3>
                <p className="text-muted-foreground text-xs mb-3 leading-relaxed min-h-[2.5rem] flex items-center">Premium komfort og støtte - 10 par inkludert</p>
                <p className="text-primary text-xl font-bold mb-4">249 kr</p>
                <div className="space-y-2 mt-auto">
                  <ShopifyBuyButton productId="9358249656551" componentId="product-component-home-tennis-socks-correct" domain="6pjebd-jw.myshopify.com" storefrontAccessToken="24c563b5bb4fa1b2d3b84fcb8e32090c" className="w-full" iframeTitle="Kjøp PEVEO Tennissokker - Legg til i handlekurv" />
                  <Button asChild variant="outline" className="w-full text-xs py-1">
                    <Link to="/products/tennis-socks">Les mer</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Peveo - Clean grid */}
      <section id="why-choose-peveo" className="py-12 md:py-16 bg-muted relative z-20">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-orbit font-bold text-foreground text-center mb-8 md:mb-12">
            Hvorfor velge <span className="gradient-text">Peveo?</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="bg-card rounded-xl p-6 border border-border shadow-sm text-center">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-orbit font-bold text-foreground mb-2">Forskningsbasert</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">Alle produkter har fagfellevurderte forskningsartikler som dokumenterer effekten.</p>
            </div>
            <div className="bg-card rounded-xl p-6 border border-border shadow-sm text-center">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-orbit font-bold text-foreground mb-2">Optimal dosering</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">Kun effektive doser basert på forskning – ikke symbolske mengder.</p>
            </div>
            <div className="bg-card rounded-xl p-6 border border-border shadow-sm text-center">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-orbit font-bold text-foreground mb-2">Best på pris</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">Vi skal alltid være billigst på sammenlignbare produkter.</p>
            </div>
            <div className="bg-card rounded-xl p-6 border border-border shadow-sm text-center">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-orbit font-bold text-foreground mb-2">For en god sak</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">Overskuddet investeres i Stoad – fremtidens treningsutstyr.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stoad / Patent Section */}
      <section className="py-12 md:py-16 bg-background relative z-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <p className="text-sm font-semibold tracking-widest uppercase text-primary mb-2">Dette går overskuddet til</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-orbit font-bold text-foreground mb-4 leading-tight">
              Smart Lokk Konsept<br />
              <span className="text-primary">& Treningsutstyr</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              Vi bygger fremtidens teknologi for kunder og utøvere – slik at du blir bedre og får det bedre.
            </p>
          </div>

          {/* Patent Showcase */}
          <LazySection rootMargin="300px" fallback={<div className="h-[600px]" />}>
            <PatentShowcase />
          </LazySection>

          {/* Innovation section */}
          <div className="mt-12 md:mt-16">
            <div className="text-center mb-8">
              <p className="text-sm font-semibold tracking-widest uppercase text-primary mb-2">Fremtidens treningsutstyr</p>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-orbit font-bold text-foreground mb-4">
                Revolusjonerende hoftefleksjon
              </h3>
              <p className="text-muted-foreground max-w-3xl mx-auto text-sm md:text-base leading-relaxed">
                Vi hjelper utøvere og pasienter med kneskader å styrke rectus femoris bedre gjennom justerbar hoftefleksjon.
              </p>
            </div>

            {/* Main Innovation Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-12">
              <div className="lg:col-span-3 relative">
                <div className="relative bg-card rounded-2xl p-4 md:p-8 border border-border shadow-sm">
                  <img src="/lovable-uploads/2b56fea2-abfd-4a12-bcda-e59504c1129d.png" alt="STEAD Stack & Load - Justerbar hoftevinkel" className="w-full max-w-full h-auto rounded-xl md:hover:scale-[1.02] transition-all duration-700" loading="lazy" />
                  <div className="absolute top-2 right-2 md:top-4 md:right-4 bg-primary px-2 md:px-4 py-1 md:py-2 rounded-full">
                    <span className="text-primary-foreground font-semibold text-xs md:text-sm">HOVEDINNOVASJON</span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 flex flex-col h-full justify-between space-y-6">
                {/* Feature 1 */}
                <div className="bg-card rounded-xl p-4 border border-border shadow-sm hover:shadow-md transition-all duration-300 flex-1">
                  <div className="flex gap-4 h-full">
                    <div className="flex-1 flex flex-col justify-center">
                      <div className="flex items-center gap-2 md:gap-3 mb-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-foreground text-sm md:text-base font-bold">1</span>
                        </div>
                        <h4 className="text-base md:text-lg font-orbit font-bold text-foreground">Spesialdesignet CAM</h4>
                      </div>
                      <p className="text-muted-foreground text-xs md:text-sm leading-relaxed">
                        Matcher quadriceps sin faktiske styrkeprofil perfekt. Revolusjonerende motstandskurve.
                      </p>
                    </div>
                    <div className="flex-1 flex items-stretch">
                      <div className="relative overflow-hidden rounded-xl border border-border w-full">
                        <img src="/lovable-uploads/eccfa753-1c0a-4168-8952-669ca5de371f.png" alt="PEVEO Spesialdesignet CAM" className="w-full h-full object-cover rounded-xl" loading="lazy" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feature 2 */}
                <div className="bg-card rounded-xl p-4 border border-border shadow-sm hover:shadow-md transition-all duration-300 flex-1">
                  <div className="flex gap-4 h-full">
                    <div className="flex-1 flex flex-col justify-center">
                      <div className="flex items-center gap-2 md:gap-3 mb-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-foreground text-sm md:text-base font-bold">2</span>
                        </div>
                        <h4 className="text-base md:text-lg font-orbit font-bold text-foreground">Full ROM</h4>
                      </div>
                      <p className="text-muted-foreground text-xs md:text-sm leading-relaxed">
                        Avbuet frontbein gir bedre bevegelsesbane for dypere strekk og optimal muskelaktivering.
                      </p>
                    </div>
                    <div className="flex-1 flex items-center">
                      <div className="relative overflow-hidden rounded-xl border border-border w-full h-full">
                        <img src="/lovable-uploads/a849cd5b-38be-409d-93e8-3d9b9575990a.png" alt="Bevegelsesanalyse" className="w-full h-full object-cover object-left rounded-xl" loading="lazy" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feature 3: 240kg */}
                <div className="bg-card rounded-xl p-6 border border-border shadow-sm hover:shadow-md transition-all duration-300 h-40">
                  <div className="flex gap-6 h-full">
                    <div className="flex-1 flex flex-col justify-center">
                      <div className="flex items-center gap-2 md:gap-3 mb-2">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-foreground text-sm md:text-base font-bold">3</span>
                        </div>
                        <h4 className="text-base md:text-lg font-orbit font-bold text-foreground">240kg Stack</h4>
                      </div>
                      <p className="text-muted-foreground text-xs md:text-sm leading-relaxed">
                        Dimensjonert for tunge løft. Maksimal quadriceps-stimuli.
                      </p>
                    </div>
                    <div className="flex-1 flex items-center justify-center" ref={counterRef}>
                      <div className="text-center flex items-center gap-2 md:gap-4">
                        <div className="text-4xl md:text-6xl lg:text-8xl font-orbit font-bold text-primary leading-none"><span ref={counterValueEl}>0</span></div>
                        <div className="text-muted-foreground text-sm md:text-lg font-semibold">KG<br />STACK</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Figure Caption */}
            <div className="max-w-4xl mx-auto px-4 mb-12">
              <div className="bg-muted rounded-xl p-6 border border-border">
                <p className="text-center text-sm md:text-base text-muted-foreground leading-relaxed">
                  <span className="font-semibold text-foreground">Figur:</span> Studier viser at effektiv dose glycerol for hyperhydrering er ca. 1,2 g/kg kroppsvekt (~84 g for 70 kg). GlycerSize/HydroMax inneholder bare ~65 % glycerol, så 3–5 g pulver gir kun 2–3 g ren glycerol – en kraftig underdosering sammenlignet med effektive nivåer.
                </p>
              </div>
            </div>

            {/* Key Benefits */}
            <div className="bg-muted rounded-2xl p-6 md:p-12 border border-border">
              <h4 className="text-2xl md:text-3xl font-orbit font-bold text-foreground mb-6 md:mb-8 text-center">Revolusjonerende hoftefleksjon aktiverer:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
                <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-sm text-center">
                  <div className="text-3xl md:text-4xl mb-2 md:mb-4">🎯</div>
                  <h5 className="text-lg md:text-xl font-semibold text-foreground mb-2">Hele Quadriceps</h5>
                  <p className="text-sm md:text-base text-muted-foreground">Inkludert rectus femoris som er relativt inaktiv i sittende posisjon</p>
                </div>
                <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-sm text-center">
                  <div className="text-3xl md:text-4xl mb-2 md:mb-4">🛡️</div>
                  <h5 className="text-lg md:text-xl font-semibold text-foreground mb-2">Kneskadeforebygging</h5>
                  <p className="text-sm md:text-base text-muted-foreground">Styrker muskler festet direkte til patella og patellasenene</p>
                </div>
                <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-sm text-center">
                  <div className="text-3xl md:text-4xl mb-2 md:mb-4">⚡</div>
                  <h5 className="text-lg md:text-xl font-semibold text-foreground mb-2">Maksimal Aktivering</h5>
                  <p className="text-sm md:text-base text-muted-foreground">Både toppidrettsutøvere og vanlige medlemmer får optimal stimuli</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section — Clean */}
      <section className="py-16 md:py-24 bg-muted relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center max-w-7xl mx-auto">
            <div className="lg:col-span-3 space-y-8">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-primary rounded-full shadow-lg">
                <div className="text-center">
                  <div className="text-3xl font-black text-primary-foreground">100%</div>
                  <div className="text-xs font-bold text-primary-foreground">GARANTI</div>
                </div>
              </div>

              <div>
              <h2 className="text-3xl md:text-5xl font-orbit font-black text-foreground mb-6 leading-tight">
                  Din <span className="text-primary">tilfredshet</span>
                  <br />er garantert
                </h2>
              </div>

              <div className="space-y-4">
                <Collapsible>
                  <div className="bg-card border border-border rounded-xl p-6 hover:shadow-md transition-all duration-300">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl">👅</div>
                      <div>
                        <h3 className="text-xl font-orbit font-bold text-foreground mb-1">Smaksgaranti</h3>
                        <p className="text-muted-foreground text-base leading-relaxed">
                          Liker du ikke smaken? Få pengene tilbake.
                        </p>
                      </div>
                    </div>
                    <CollapsibleTrigger className="mt-3 text-sm text-primary hover:underline cursor-pointer transition-colors">
                      Se vilkår
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 text-muted-foreground text-sm leading-relaxed border-t border-border pt-3">
                      Garantien gjelder for uåpnede bokser som returneres i original emballasje. Kontakt oss på kundeservice@peveo.no for retur.
                    </CollapsibleContent>
                  </div>
                </Collapsible>

                <Collapsible>
                  <div className="bg-card border border-border rounded-xl p-6 hover:shadow-md transition-all duration-300">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl">⚡</div>
                      <div>
                        <h3 className="text-xl font-orbit font-bold text-foreground mb-1">Effektgaranti</h3>
                        <p className="text-muted-foreground text-base leading-relaxed">
                          Merker du ikke forskjell? Pengene dine er trygt i behold hos oss.
                        </p>
                      </div>
                    </div>
                    <CollapsibleTrigger className="mt-3 text-sm text-primary hover:underline cursor-pointer transition-colors">
                      Se vilkår
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 text-muted-foreground text-sm leading-relaxed border-t border-border pt-3">
                      Garantien gjelder for uåpnede bokser som returneres i original emballasje. Kontakt oss på kundeservice@peveo.no for retur.
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              </div>

              <div className="space-y-3">
                <p className="text-foreground text-lg leading-relaxed">
                  Ingen andre gir deg produkter av denne kvaliteten til denne prisen.
                </p>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Samtidig finansierer du startup-selskapet Stoad, som bygger verdens beste treningsmaskiner for muskelvekst.
                </p>
                <p className="text-foreground text-xl font-orbit font-bold">
                  Hva venter du på?
                </p>
              </div>

              <div className="space-y-3">
                <Button size="lg" className="w-full px-8 py-4 text-lg font-bold hover:scale-[1.02] transition-all duration-200" onClick={scrollToProducts}>
                  🚀 Få dine produkter nå
                </Button>
                <Button size="lg" variant="outline" className="w-full px-8 py-4 text-lg font-bold hover:scale-[1.02] transition-all duration-200" onClick={() => window.open('https://stoadfitness.com', '_blank')}>
                  🏋️ Se fremtidens treningsutstyr
                </Button>
                <p className="text-muted-foreground text-sm font-medium text-center">
                  ⏰ Begrenset lager - sikre deg produktene før de er utsolgt
                </p>
              </div>
            </div>

            {/* Video */}
            <div className="lg:col-span-2 relative">
              <div className="relative w-full aspect-[9/14] rounded-2xl overflow-hidden shadow-xl">
                <div className="absolute inset-0 z-10 pointer-events-none">
                  <div className="absolute bottom-2 right-2 w-16 h-6 bg-black z-20 rounded"></div>
                  <div className="absolute top-2 right-2 w-12 h-4 bg-black z-20 rounded"></div>
                </div>
                <div className="absolute inset-0 z-30 pointer-events-auto cursor-default"></div>
                <LazySection rootMargin="200px" fallback={<div className="absolute inset-0 bg-muted rounded-2xl" />}>
                  <iframe
                    src="https://www.youtube.com/embed/hD6VwfI-cXI?autoplay=1&mute=1&loop=1&playlist=hD6VwfI-cXI&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=0&vq=small&hd=0&quality=small"
                    title="PEVEO Product Demonstration Video"
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none scale-110"
                    allow="autoplay; encrypted-media"
                    allowFullScreen={false}
                    style={{ pointerEvents: 'none' }}
                    frameBorder="0"
                  ></iframe>
                </LazySection>
              </div>
              <p className="mt-4 text-center text-muted-foreground text-sm">Se våre produkter i aksjon</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pre-footer */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4 text-center space-y-6">
          <h3 className="text-2xl md:text-3xl font-orbit font-bold text-foreground">
            Takk for at du velger <span className="gradient-text">PEVEO</span>
          </h3>
          <p className="text-muted-foreground text-base max-w-xl mx-auto">
            Sammen bygger vi fremtiden for trening og helse. Hver bestilling bidrar til noe større.
          </p>

          <div className="flex flex-wrap justify-center gap-6 items-center text-muted-foreground">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <span className="text-sm">100% Sikker betaling</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              <span className="text-sm">Rask levering</span>
            </div>
            <div className="flex items-center gap-2">
              <HeartHandshake className="h-5 w-5" />
              <span className="text-sm">For en god sak</span>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <p className="text-muted-foreground text-xs mb-3">Aksepterte betalingsmåter:</p>
            <div className="flex flex-wrap justify-center items-center gap-3 text-muted-foreground/60 text-xs">
              <span>Visa</span><span>•</span>
              <span>Mastercard</span><span>•</span>
              <span>American Express</span><span>•</span>
              <span>Apple Pay</span><span>•</span>
              <span>Google Pay</span><span>•</span>
              <span>PayPal</span><span>•</span>
              <span>Shop Pay</span><span>•</span>
              <span>Klarna</span><span>•</span>
              <span>Discover</span><span>•</span>
              <span>Diners Club</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
};

export default Home;
